import { tool } from '@openai/agents/realtime';
import { VOICE_TOOL_DESCRIPTIONS } from '@/lib/live-voice-tool-definitions';
import { workspaceCommandSchema } from '@/lib/voice-call-tool-input';
import type { RealtimeVoiceToolContext } from './realtime-voice-tool-context';

const INACTIVE_CALL =
  'Internal safety: this voice call is no longer active. Stay silent.';

/** Read-only workspace commands; the server allowlist decides what may run. */
export function createRealtimeVoiceWorkspaceTool(
  context: Pick<
    RealtimeVoiceToolContext,
    | 'isCurrentCall'
    | 'activeOperationRef'
    | 'getCurrentWorkspaceLocation'
    | 'runWorkspaceCommand'
    | 'orgThreadId'
    | 'leaseSessionId'
    | 'finishRealtimeToolResult'
  >
) {
  return tool({
    name: 'workspace_command',
    description: VOICE_TOOL_DESCRIPTIONS.workspace_command,
    parameters: workspaceCommandSchema,
    strict: true,
    errorFunction: null,
    execute: async (input) => {
      if (!context.isCurrentCall()) return INACTIVE_CALL;
      if (!context.activeOperationRef.current) {
        return 'Workspace commands require a new spoken user request.';
      }
      const { command } = workspaceCommandSchema.parse(input);
      try {
        const location = context.getCurrentWorkspaceLocation();
        const { result } = await context.runWorkspaceCommand({
          orgThreadId: context.orgThreadId,
          sessionId: context.leaseSessionId,
          command,
          cwd: location.path,
          ...(location.nodeReference
            ? { cwdReference: location.nodeReference }
            : {}),
        });
        if (!context.isCurrentCall()) return INACTIVE_CALL;
        return context.finishRealtimeToolResult({
          toolName: 'workspace_command',
          result,
        });
      } catch (error) {
        if (!context.isCurrentCall()) return INACTIVE_CALL;
        console.warn('[voice-call] workspace command failed', {
          errorName: error instanceof Error ? error.name : 'UnknownError',
        });
        return context.finishRealtimeToolResult({
          toolName: 'workspace_command',
          result:
            'The workspace command did not run. Tell the user briefly; do not guess the folder contents.',
        });
      }
    },
  });
}
